import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { usePopperTooltip, TriggerType } from 'react-popper-tooltip'
import LinkButton from '@/components/atoms/LinkButton/LinkButton'
import { isString } from '@/utils/checks'
import * as PopperJS from '@popperjs/core'

export interface PopoverProps {
  header: string | ReactNode
  description: string
  buttonLinkText?: string
  buttonLinkTarget?: string
  buttonLinkIcon?: ReactNode
  placement?: PopperJS.Placement
  delay?: number
  trigger?: TriggerType | TriggerType[] | null
  distance?: number
  hidden?: boolean
  className?: string
  children: ReactNode
}

const Popover: FC<PopoverProps> = ({
  header,
  description = '',
  buttonLinkText = '',
  buttonLinkTarget = '',
  buttonLinkIcon = '',
  placement = 'bottom-start',
  delay = 0,
  trigger = 'click',
  distance = 6,
  hidden = false,
  className = '',
  children
}) => {
  const [controlledVisible, setControlledVisible] = useState(false)
  const [actualPlacement, setActualPlacement] = useState(placement)
  const popoverElement = useRef<HTMLDivElement>(null)

  const { getTooltipProps, setTooltipRef, setTriggerRef, visible } =
    usePopperTooltip({
      delayShow: delay,
      offset: [0, distance],
      trigger,
      closeOnTriggerHidden: true,
      visible: controlledVisible,
      onVisibleChange: setControlledVisible,
      placement: placement
    })

  useEffect(() => {
    if (visible && popoverElement.current) {
      const computedPlacement = popoverElement.current.dataset.popperPlacement
      if (computedPlacement) {
        setActualPlacement(computedPlacement as PopperJS.Placement)
      }
    }
  }, [visible])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setControlledVisible(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  if (hidden) {
    return <>{children}</>
  }

  const hasLink = buttonLinkTarget && buttonLinkText

  const handleMouseEnter = () => {
    setControlledVisible(true)
  }

  const handleMouseLeave = () => {
    setTimeout(() => {
      if (popoverElement.current && !popoverElement.current.matches(':hover')) {
        setControlledVisible(false)
      }
    }, 100)
  }

  const getTransformOrigin = (placement: string): string => {
    const [position, align] = placement.split('-')
    switch (position) {
      case 'top':
        if (align === 'start') return 'origin-bottom-left'
        if (align === 'end') return 'origin-bottom-right'
        return 'origin-bottom'
      case 'bottom':
        if (align === 'start') return 'origin-top-left'
        if (align === 'end') return 'origin-top-right'
        return 'origin-top'
      case 'left':
        if (align === 'start') return 'origin-right-top'
        if (align === 'end') return 'origin-right-bottom'
        return 'origin-right'
      case 'right':
        if (align === 'start') return 'origin-left-top'
        if (align === 'end') return 'origin-left-bottom'
        return 'origin-left'
      default:
        return 'origin-top-left'
    }
  }

  return (
    <>
      <div
        className="block"
        ref={setTriggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      <div
        ref={(node) => {
          setTooltipRef(node)
          popoverElement.current = node
        }}
        {...getTooltipProps({
          className: `z-40 ${
            visible
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          } transition-opacity duration-200 ease-in-out`
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`flex w-[324px] self-stretch px-6 pt-6 ${
            hasLink ? 'pb-5' : 'pb-6'
          } ${getTransformOrigin(
            actualPlacement
          )} rounded-xl border border-gray-300 bg-white shadow-lg transition-all duration-200 ease-in-out ${
            visible ? 'scale-100' : 'scale-[80%]'
          } ${className}`}
        >
          <div className="flex cursor-default flex-col gap-4">
            <div className="flex flex-col items-start gap-2">
              {isString(header) ? (
                <h3 className="text-sm font-semibold text-gray-1100">
                  {header}
                </h3>
              ) : (
                header
              )}
              {isString(description) ? (
                <p className="text-sm text-gray-800">{description}</p>
              ) : (
                description
              )}
            </div>
            {hasLink && (
              <div>
                <LinkButton
                  label={buttonLinkText}
                  href={buttonLinkTarget}
                  iconElement={buttonLinkIcon}
                  onClick={() => setControlledVisible(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Popover
